import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { BaseResource } from '../../types';
import { useWarbandStore } from '../../stores/warbandStore';
import { useDirtyTrackingStore } from '../../stores/dirtyTrackingStore';
import { useReferenceDataStore } from '../../stores/referenceDataStore';

interface ManageBaseResourcesModalProps {
  open: boolean;
  onClose: () => void;
}

const BaseResourceListItem = React.memo<{
  resource: BaseResource;
  selected: boolean;
  owned: boolean;
  onToggle: (id: string) => void;
}>(({ resource, selected, owned, onToggle }) => (
  <ListItemButton
    onClick={() => onToggle(resource.id)}
    disabled={owned}
    sx={{ opacity: owned ? 0.6 : 1 }}
  >
    <ListItemIcon>
      <Checkbox
        edge="start"
        checked={selected || owned}
        tabIndex={-1}
        disableRipple
      />
    </ListItemIcon>
    <ListItemText
      primary={
        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {resource.name}
          {owned && (
            <Typography variant="caption" color="text.secondary" component="span">
              (owned)
            </Typography>
          )}
        </Box>
      }
      secondary={
        <Box component="span">
          {resource.cost != null && (
            <Typography variant="caption" component="span" color="text.secondary">
              Cost: {resource.cost}gc
            </Typography>
          )}
          {resource.description && (
            <Typography variant="caption" component="span" sx={{ display: 'block', mt: 0.5 }}>
              {resource.description}
            </Typography>
          )}
        </Box>
      }
    />
  </ListItemButton>
));

export const ManageBaseResourcesModal: React.FC<ManageBaseResourcesModalProps> = React.memo(({
  open,
  onClose,
}) => {
  const baseResources = useWarbandStore((s) => s.baseResources);
  const gold = useWarbandStore((s) => s.warbands.find((w) => w.id === s.selectedWarbandId)?.gold ?? 0);
  const updateBaseResources = useDirtyTrackingStore((s) => s.updateBaseResources);
  const rawAllResources = useReferenceDataStore((s) => s.allBaseResources);
  const allResources = React.useMemo(
    () => [...rawAllResources].sort((a, b) => a.name.localeCompare(b.name)),
    [rawAllResources]
  );
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [isFree, setIsFree] = useState(false);

  const ownedIds = React.useMemo(
    () => new Set(baseResources.map((r) => r.id)),
    [baseResources]
  );

  React.useEffect(() => {
    if (!open) return;
    setSelectedIds(new Set());
    setFilter('');
    setError(null);
    setIsFree(false);
  }, [open]);

  const handleClose = () => {
    setFilter('');
    setError(null);
    setIsFree(false);
    onClose();
  };

  const handleSave = () => {
    const existingIds = baseResources.map((r) => r.id);
    const newIds = Array.from(selectedIds);
    updateBaseResources([...existingIds, ...newIds], isFree);
    handleClose();
  };

  const handleToggle = useCallback((resourceId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(resourceId)) {
        next.delete(resourceId);
      } else {
        next.add(resourceId);
      }
      return next;
    });
  }, []);

  const filteredResources = React.useMemo(() => {
    const lower = filter.trim().toLowerCase();
    if (!lower) return allResources;
    return allResources.filter(
      (r) =>
        r.name.toLowerCase().includes(lower) ||
        r.description.toLowerCase().includes(lower)
    );
  }, [allResources, filter]);

  const totalCost = React.useMemo(() => {
    let cost = 0;
    Array.from(selectedIds).forEach((id) => {
      const resource = allResources.find((r) => r.id === id);
      if (resource?.cost != null) cost += resource.cost;
    });
    return cost;
  }, [selectedIds, allResources]);

  const canAfford = isFree || totalCost === 0 || gold >= totalCost;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Add Base Resources</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}

        {allResources.length > 0 && (
          <>
            <TextField
              size="small"
              placeholder="Filter resources..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              fullWidth
              sx={{ mt: 1 }}
            />
            <Typography variant="caption" color={!isFree && !canAfford ? 'error' : 'textSecondary'}>
              {selectedIds.size} resource{selectedIds.size !== 1 ? 's' : ''} to add
              {totalCost > 0 && ` (${totalCost}gc)`}
              {!isFree && !canAfford && ` — insufficient gold (have ${gold}gc)`}
            </Typography>
            <List
              dense
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                height: 400,
                overflowY: 'auto',
                py: 0,
              }}
            >
              {filteredResources.map((resource) => (
                <BaseResourceListItem
                  key={resource.id}
                  resource={resource}
                  selected={selectedIds.has(resource.id)}
                  owned={ownedIds.has(resource.id)}
                  onToggle={handleToggle}
                />
              ))}
              {filteredResources.length === 0 && (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No resources{filter ? ' matching the filter' : ''}.
                  </Typography>
                </Box>
              )}
            </List>
          </>
        )}

        {allResources.length === 0 && !error && (
          <Typography variant="body2" color="text.secondary">
            No base resources available.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <FormControlLabel
          control={
            <Checkbox
              checked={isFree}
              onChange={(e) => setIsFree(e.target.checked)}
              size="small"
              disabled={selectedIds.size === 0}
            />
          }
          label="Add for free"
          sx={{ mr: 'auto' }}
        />
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={selectedIds.size === 0 || (!isFree && !canAfford)}
        >
          Add Resources
        </Button>
      </DialogActions>
    </Dialog>
  );
});
